import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Accordion, Box, get, useAccordionItem } from '@ally/metronome-ui'
import { useLocation } from 'react-router-dom'
import { useAccountDetailsContext } from '../../services'
import { CoverDraftRedirectLocationState } from '../../types'

// Styled AccordionWrapper for all account types - ensure consistent padding
export const AcctDetailsAccordionWrapper = styled(Box)`
  padding-left: ${get.space('md')};
  padding-right: ${get.space('xxs')};
  padding-bottom: ${get.space('xxs')};
  padding-top: ${get.space('xxs')};
`

const AcctDetailAccordionItem = styled.div`
  display: flex;
  flex-flow: row wrap;
  width: 100%;
  padding: 0;
  margin: 0;
  border: 0;
`
const StyledAcctDetailsAccordion = styled(Box)`
  display: flex;
  flex-flow: row wrap;
`

interface AccordionPanelProps {
  accordionContents: Array<React.ReactElement | boolean>[]
  accordionMessage?: React.ReactElement | null
  handleAccordionToggle?: (val: boolean) => void
}

const PanelContent: React.FC<AccordionPanelProps> = ({
  accordionContents,
  accordionMessage,
  handleAccordionToggle,
}) => {
  const { isExpanded } = useAccordionItem()
  useEffect(() => {
    // No real logic here
    /* istanbul ignore next */
    if (handleAccordionToggle) {
      handleAccordionToggle(isExpanded)
    }
  }, [isExpanded])

  return (
    <StyledAcctDetailsAccordion mt="xs" data-testid="account-details-panel">
      {accordionMessage}
      <AcctDetailAccordionItem>
        {accordionContents[0].map(
          (x: React.ReactElement | boolean, i: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={i}>{x}</React.Fragment>
          ),
        )}
      </AcctDetailAccordionItem>
      <AcctDetailAccordionItem>
        {accordionContents[1].map(
          (x: React.ReactElement | boolean, i: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={i}>{x}</React.Fragment>
          ),
        )}
      </AcctDetailAccordionItem>
    </StyledAcctDetailsAccordion>
  )
}
// Summary Accordion panel for all account types
export const AcctDetailsAccordionSummaryPanel: React.FC<AccordionPanelProps> =
  ({ accordionContents, accordionMessage, handleAccordionToggle }) => {
    const [clickEventName, setClickEventName] = useState('')
    const { accountDetails } = useAccountDetailsContext()
    const { state: locationState } =
      useLocation<CoverDraftRedirectLocationState>()
    const { retirementAccountIndicator, accountType } =
      accountDetails || // istanbul ignore next // low value
      {}

    // Callback that will passthrough the optional onAccordionToggle and setClickEvent name for tracking
    const onAccordionToggle = (isExpanded: boolean): void => {
      // Optional callback that is currently only passed in on Checking Accounts for Coverdraft
      // istanbul ignore next
      if (handleAccordionToggle) {
        handleAccordionToggle(isExpanded)
      }
      /*
       * Updating the clickEventName happens after the event is fired.
       * To counteract this, set the clickEventName to what it SHOULD be when the drawer is clicked
       * This will be the opposite of isExpanded
       */
      // Low value coverage
      /* istanbul ignore next */
      setClickEventName(
        !isExpanded
          ? 'accountDetails-openDrawer'
          : 'accountDetails-closeDrawer',
      )
    }

    // we want to expand account details accordion by default only for CD and IRA accounts
    return (
      <Accordion
        disableContainer
        items={[
          {
            heading: 'Account Details',
            panel: (
              <PanelContent
                accordionContents={accordionContents}
                handleAccordionToggle={onAccordionToggle}
                accordionMessage={accordionMessage}
              />
            ),
            clickEventName,
            dataTracking: { trigger: 'acctDetailsButton' },
            isOpenOnMount:
              retirementAccountIndicator ||
              accountType === 'CDA' ||
              locationState?.openCoverDraftSettings,
          },
        ]}
      />
    )
  }
